/**
 * Offerte Detail/Print Pagina
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { offertesService, Offerte } from '../../services/offertes.service';
import { bedrijfsgegevensService, Bedrijfsgegevens } from '../../services/bedrijfsgegevens.service';
import { relationsService, Relation } from '../../services/relations.service';
import { getApiBaseUrl } from '../../services/api';
import VerzendModal from '../../components/VerzendModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import Toast from '../../components/Toast';
import '../../components/shared/briefhoofd.css';
import '../../components/shared/document.css';
import './OfferteDetail.css';

interface OfferteDetailProps {
  offerteId: number;
}

const OfferteDetail = ({ offerteId }: OfferteDetailProps) => {
  const navigate = useNavigate();
  const [offerte, setOfferte] = useState<Offerte | null>(null);
  const [bedrijf, setBedrijf] = useState<Bedrijfsgegevens | null>(null);
  const [klant, setKlant] = useState<Relation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVerzendModal, setShowVerzendModal] = useState(false);
  const [verzending, setVerzending] = useState(false);
  const [verzendSuccess, setVerzendSuccess] = useState<string | null>(null);
  const [verzendError, setVerzendError] = useState<string | null>(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [showPDFConfirm, setShowPDFConfirm] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerteId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [offerteData, bedrijfData] = await Promise.all([
        offertesService.getById(offerteId),
        bedrijfsgegevensService.get()
      ]);

      setOfferte(offerteData);
      setBedrijf(bedrijfData);

      // Laad klantgegevens
      const klantData = await relationsService.getById(offerteData.relatie_id);
      setKlant(klantData);

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon offerte niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleVerzenden = () => {
    // Toon direct het verzend modal
    // Backend verwacht dat PDF al is opgeslagen in documents/B/[Klant]/[Offertenummer].pdf
    setShowVerzendModal(true);
  };

  const handleGenereerPDF = () => {
    setShowPDFConfirm(true);
  };

  const handleConfirmGenereerPDF = async () => {
    setShowPDFConfirm(false);
    try {
      setGeneratingPDF(true);
      setVerzendError(null);

      await offertesService.genereerPDF(offerteId);

      setVerzendSuccess('PDF succesvol gegenereerd!');
      setTimeout(() => setVerzendSuccess(null), 3000);
    } catch (err: any) {
      setVerzendError(err.message || 'Fout bij genereren PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleConfirmVerzenden = async (email: string) => {
    try {
      setVerzending(true);
      setVerzendError(null);
      setVerzendSuccess(null);

      const result = await offertesService.verzenden(offerteId, email);

      setVerzendSuccess(`Offerte succesvol verzonden naar ${result.email}`);
      setShowVerzendModal(false);

      // Reload offerte om status update te tonen
      await loadData();

      // Verberg success message na 5 seconden
      setTimeout(() => setVerzendSuccess(null), 5000);
    } catch (err: any) {
      setVerzendError(err.message || 'Fout bij verzenden offerte');
      setShowVerzendModal(false);
    } finally {
      setVerzending(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    // Als het een heel getal is (bijv. 8.00), toon alleen het hele getal
    if (num % 1 === 0) {
      return Math.floor(num).toString();
    }
    // Anders toon met decimalen, maar verwijder onnodige nullen
    return num.toString().replace(/\.?0+$/, '');
  };

  const getKlantNaam = () => {
    if (!klant) return '';
    if (klant.bedrijfsnaam === 'Particulier') {
      const naamParts = [
        klant.aanhef,
        klant.voornaam ? `${klant.voornaam.charAt(0)}.` : undefined,
        klant.tussenvoegsel,
        klant.achternaam
      ].filter(Boolean);
      return naamParts.join(' ');
    }
    return klant.bedrijfsnaam;
  };

  if (loading) {
    return (
      <div className="offerte-detail-container">
        <div className="loading">Offerte laden...</div>
      </div>
    );
  }

  if (error || !offerte || !bedrijf || !klant) {
    return (
      <div className="offerte-detail-container">
        <div className="error-message">{error || 'Offerte niet gevonden'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/offertes')}>
          Terug naar Offertes
        </button>
      </div>
    );
  }

  // Splits calculaties op in pagina's gebaseerd op beschikbare ruimte
  const renderPages = () => {
    const pages = [];
    const calculaties = offerte.calculaties && offerte.calculaties.length > 0 ? offerte.calculaties : [];
    
    if (calculaties.length === 0) {
      // Geen calculaties - render legacy regels
      pages.push(
        <div key="page-1" className="offerte-document">
          {renderHeader(1)}
          <div className="offerte-content">
            {renderLegacyRegels()}
          </div>
        </div>
      );
      return pages;
    }

    // Regel-gebaseerde paginering afgestemd op werkelijke A4 ruimte
    const MAX_REGELS_PER_PAGINA = 28; // Meer witte ruimte aan onderkant pagina
    
    let currentPageRegels = 0;
    let currentPageContent: any[] = [];
    let pageNumber = 1;
    let totalRegels = 0;

    // Tel totaal aantal regels voor pagina berekening
    calculaties.forEach(calc => {
      if (calc.actief && calc.regels) {
        totalRegels += calc.regels.filter((regel: any) => regel.actief).length;
      }
    });

    // Schat totaal aantal pagina's (+ 1 voor totalen)
    const estimatedTotalPages = Math.ceil(totalRegels / MAX_REGELS_PER_PAGINA) + 1;

    // Eerste pagina met header
    let isFirstPage = true;
    
    calculaties.forEach((calculatie, calcIndex) => {
      if (!calculatie.actief) return;

      // Check if we need new page before adding calculatie header
      if (currentPageRegels > 0 && currentPageRegels + 3 > MAX_REGELS_PER_PAGINA) {
        // Create current page
        const pageHeader = isFirstPage ? 
          renderHeader(estimatedTotalPages) : 
          renderCompactHeader(pageNumber, estimatedTotalPages);

        pages.push(
          <div key={`page-${pageNumber}`} className="offerte-document">
            {pageHeader}
            <div className="offerte-content">
              {currentPageContent}
            </div>
          </div>
        );

        // Reset for next page
        currentPageContent = [];
        currentPageRegels = 0;
        pageNumber++;
        isFirstPage = false;
      }

      // Add calculatie header to current page
      const calculatieHeader = (
        <div key={`calc-${pageNumber}-${calcIndex}`} className="calculatie-section">
          <h2 className="calculatie-title">Sectie {calculatie.naam}</h2>
          {calculatie.omschrijving && (
            <div className="calculatie-description">{calculatie.omschrijving}</div>
          )}
        </div>
      );
      
      currentPageContent.push(calculatieHeader);
      currentPageRegels += 2; // Header takes ~2 lines

      const arbeidRegels = calculatie.regels?.filter((regel: any) => regel.actief && regel.type_regel === 'ARBEID') || [];
      const materiaalRegels = calculatie.regels?.filter((regel: any) => regel.actief && ['MATERIAAL', 'ONDERDEEL', 'UITBESTEED', 'TEKST'].includes(regel.type_regel)) || [];

      // Split werkzaamheden if needed
      if (arbeidRegels.length > 0) {
        const remainingSpace = MAX_REGELS_PER_PAGINA - currentPageRegels - 3; // Reserve 3 for subtotal
        
        if (arbeidRegels.length <= remainingSpace) {
          // Fits on current page
          const werkzaamhedenSection = renderRegelSection('Werkzaamheden', arbeidRegels, true);
          currentPageContent.push(werkzaamhedenSection);
          currentPageRegels += arbeidRegels.length + 2; // +2 for headers
        } else {
          // Need to split - put what fits on current page
          const regelsThisPage = arbeidRegels.slice(0, remainingSpace);
          const regelsNextPage = arbeidRegels.slice(remainingSpace);
          
          if (regelsThisPage.length > 0) {
            const werkzaamhedenSection = renderRegelSection('Werkzaamheden', regelsThisPage, false); // Geen subtotaal bij gesplitste sectie
            currentPageContent.push(werkzaamhedenSection);
            currentPageRegels += regelsThisPage.length + 2;
          }

          // Create new page for remaining rules
          if (regelsNextPage.length > 0) {
            // Finish current page
            const pageHeader = isFirstPage ? 
              renderHeader(estimatedTotalPages) : 
              renderCompactHeader(pageNumber, estimatedTotalPages);

            pages.push(
              <div key={`page-${pageNumber}`} className="offerte-document">
                {pageHeader}
                <div className="offerte-content">
                  {currentPageContent}
                </div>
              </div>
            );

            // Start new page
            currentPageContent = [];
            currentPageRegels = 0;
            pageNumber++;
            isFirstPage = false;

            // Add remaining werkzaamheden to new page
            const werkzaamhedenSectionContinued = renderRegelSection('Werkzaamheden (vervolg)', regelsNextPage, true);
            currentPageContent.push(werkzaamhedenSectionContinued);
            currentPageRegels += regelsNextPage.length + 2;
          }
        }
      }

      // Handle materialen similarly
      if (materiaalRegels.length > 0) {
        const remainingSpace = MAX_REGELS_PER_PAGINA - currentPageRegels - 3; // Reserve 3 for subtotal
        
        if (materiaalRegels.length <= remainingSpace) {
          const materialenSection = renderRegelSection('Materialen en Uitbesteed werk', materiaalRegels, true);
          currentPageContent.push(materialenSection);
          currentPageRegels += materiaalRegels.length + 2;
        } else {
          // Need to split materialen too
          const regelsThisPage = materiaalRegels.slice(0, remainingSpace);
          const regelsNextPage = materiaalRegels.slice(remainingSpace);
          
          if (regelsThisPage.length > 0) {
            const materialenSection = renderRegelSection('Materialen en Uitbesteed werk', regelsThisPage, false); // Geen subtotaal bij gesplitste sectie
            currentPageContent.push(materialenSection);
            currentPageRegels += regelsThisPage.length + 2;
          }

          if (regelsNextPage.length > 0) {
            // Finish current page
            const pageHeader = isFirstPage ? 
              renderHeader(estimatedTotalPages) : 
              renderCompactHeader(pageNumber, estimatedTotalPages);

            pages.push(
              <div key={`page-${pageNumber}`} className="offerte-document">
                {pageHeader}
                <div className="offerte-content">
                  {currentPageContent}
                </div>
              </div>
            );

            // Start new page
            currentPageContent = [];
            currentPageRegels = 0;
            pageNumber++;
            isFirstPage = false;

            const materialenSectionContinued = renderRegelSection('Materialen en Uitbesteed werk (vervolg)', regelsNextPage, true);
            currentPageContent.push(materialenSectionContinued);
            currentPageRegels += regelsNextPage.length + 2;
          }
        }
      }

      // Add calculatie subtotaal
      const subtotaal = (
        <div key={`subtotal-${pageNumber}-${calcIndex}`} className="calculatie-subtotal">
          <span>Totaal {calculatie.naam} excl. BTW</span>
          <strong>{formatCurrency(Number(calculatie.subtotaal) || 0)}</strong>
        </div>
      );
      currentPageContent.push(subtotaal);
      currentPageRegels += 1;
    });

    // Add remaining content to final page, check if totalen fit
    if (currentPageContent.length > 0) {
      const pageHeader = isFirstPage ? 
        renderHeader(estimatedTotalPages) : 
        renderCompactHeader(pageNumber, estimatedTotalPages);

      // Check if totalen still fit on this page (totalen take ~5 lines)
      if (currentPageRegels + 5 <= MAX_REGELS_PER_PAGINA) {
        // Totalen fit on current page
        pages.push(
          <div key={`page-${pageNumber}`} className="offerte-document">
            {pageHeader}
            <div className="offerte-content">
              {currentPageContent}
              {renderTotalen()}
              {renderFooter()}
            </div>
          </div>
        );
      } else {
        // Totalen need separate page
        pages.push(
          <div key={`page-${pageNumber}`} className="offerte-document">
            {pageHeader}
            <div className="offerte-content">
              {currentPageContent}
            </div>
          </div>
        );
        pageNumber++;
        
        // Separate totalen page
        pages.push(
          <div key={`page-${pageNumber}`} className="offerte-document">
            {renderCompactHeader(pageNumber, estimatedTotalPages)}
            <div className="offerte-content">
              {renderTotalen()}
              {renderFooter()}
            </div>
          </div>
        );
      }
    } else {
      // No content, just totalen page
      pages.push(
        <div key={`page-${pageNumber}`} className="offerte-document">
          {renderCompactHeader(pageNumber, estimatedTotalPages)}
          <div className="offerte-content">
            {renderTotalen()}
            {renderFooter()}
          </div>
        </div>
      );
    }

    return pages;
  };

  // Helper function for compact header
  const renderCompactHeader = (currentPage: number, totalPages: number) => (
    <div className="offerte-header-compact">
      <div className="header-left-compact">
        <img
          src={`${getApiBaseUrl()}/api/v1/files/logo`}
          alt={bedrijf.bedrijfsnaam || 'Logo'}
          className="company-logo-compact"
        />
      </div>
      <div className="page-info">
        <div><strong>Kostenraming:</strong> {offerte.offertenummer}</div>
        <div><strong>Pagina:</strong> {currentPage} van {totalPages}</div>
      </div>
    </div>
  );

  // Helper function for regel sections
  const renderRegelSection = (title: string, regels: any[], showSubtotal: boolean = true) => {
    const subtotaal = regels.reduce((sum, regel) => sum + (Number(regel.totaal) || 0), 0);
    
    return (
      <div key={`section-${title}`} className="werkzaamheden">
        <table className="werkzaamheden-tabel">
          <thead>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              <th></th>
              <th className="prijs-col">Prijs p.e.</th>
              <th className="bedrag-col">Bedrag</th>
            </tr>
            <tr>
              <th colSpan={4}>{title}</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {regels.map((regel: any, idx: number) => (
              <tr key={idx}>
                <td className="prijs-col">{formatNumber(regel.aantal)}</td>
                <td className="prijs-col">{regel.eenheid || ''}</td>
                <td></td>
                <td>{regel.omschrijving}</td>
                <td className="prijs-col">{formatCurrency(Number(regel.prijs_per_eenheid) || 0)}</td>
                <td className="bedrag-col" style={{fontWeight: 'normal'}}>{formatCurrency(Number(regel.totaal) || 0)}</td>
              </tr>
            ))}
            {showSubtotal && (
              <>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td className="bedrag-col" style={{fontWeight: 'normal'}}>========</td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td colSpan={2} style={{textAlign: 'right', fontWeight: 'bold'}}>Subtotaal excl. BTW:</td>
                  <td className="bedrag-col" style={{fontWeight: 'bold'}}>{formatCurrency(subtotaal)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCompanyHeader = () => {
    // Logo tonen als het bestaat, anders Buro42 tekst (op basis van onError)
    const logoUrl = `${getApiBaseUrl()}/api/v1/files/logo`;

    return (
      <div className="offerte-header">
        <div className="header-left">
          {!logoError ? (
            <img
              src={logoUrl}
              alt={bedrijf.bedrijfsnaam || 'Logo'}
              className="company-logo"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="company-name-large">
              <span className="buro">Buro</span>
              <span className="vier">42</span>
            </div>
          )}
        </div>

        <div className="header-right">
          <div className="company-details">
            <strong>{bedrijf.bedrijfsnaam}</strong>
            {bedrijf.straat && bedrijf.huisnummer && (
              <div>{bedrijf.straat} {bedrijf.huisnummer}</div>
            )}
            {bedrijf.postcode && bedrijf.plaats && (
              <div>{bedrijf.postcode} {bedrijf.plaats}</div>
            )}
            <div className="spacer"></div>
            {bedrijf.telefoon && <div>Tel. {bedrijf.telefoon}</div>}
            {bedrijf.email && <div>E-mail. {bedrijf.email}</div>}
            {bedrijf.website && <div>Web. {bedrijf.website}</div>}
            <div className="spacer"></div>
            {bedrijf.kvk_nummer && <div>KvK-nummer {bedrijf.kvk_nummer}</div>}
            {bedrijf.btw_nummer && <div>Btw-id. {bedrijf.btw_nummer}</div>}
          </div>
        </div>
      </div>
    );
  };


  const renderHeader = (totalPages: number) => (
    <>
      {renderCompanyHeader()}

      {/* Klantgegevens */}
      <div className="customer-details">
        <strong>{getKlantNaam()}</strong>
        {klant.straat && klant.huisnummer && (
          <div>{klant.straat} {klant.huisnummer}</div>
        )}
        {klant.postcode && klant.plaats && (
          <div>{klant.postcode} {klant.plaats}</div>
        )}
      </div>

      {/* Offerte titel en info */}
      <div className="offerte-title-section">
        <h1>
          <span className="offerte-title">Kostenraming</span>
        </h1>
        <div className="offerte-info">
          <div><strong>Nummer:</strong> {offerte.offertenummer}</div>
          <div><strong>Datum:</strong> {formatDate(offerte.offerte_datum)}</div>
          <div><strong>Geldig tot:</strong> {formatDate(offerte.geldig_tot)}</div>
          {totalPages > 1 && <div><strong>Pagina:</strong> 1 van {totalPages}</div>}
        </div>
      </div>

      {/* Omschrijving verborgen - alleen voor intern gebruik */}
    </>
  );


  const renderTotalen = () => (
    <div className="offerte-totals">
      <div className="total-box">
        <div className="total-label">Totaalprijs excl. BTW:</div>
        <div className="total-amount">{formatCurrency(Number(offerte.subtotaal) || 0)}</div>
      </div>
      <div className="total-box">
        <div className="total-label">Totaal aan BTW {Math.round(offerte.btw_percentage)}%:</div>
        <div className="total-amount">{formatCurrency(Number(offerte.btw_bedrag) || 0)}</div>
      </div>
      <div className="total-box total-final">
        <div className="total-label">Totaal prijs incl. BTW:</div>
        <div className="total-amount">{formatCurrency(Number(offerte.totaal) || 0)}</div>
      </div>
    </div>
  );

  const renderFooter = () => (
    <>
      {bedrijf.factuur_footer && (
        <div className="offerte-footer">
          {bedrijf.factuur_footer}
        </div>
      )}
    </>
  );

  const renderLegacyRegels = () => (
    <>
      {/* Backwards compatibility code hier */}
    </>
  );

  return (
    <div className="offerte-detail-container">
      {/* Actie knoppen (niet printen) */}
      <div className="print-hide action-buttons">
        <button className="btn btn-purple" onClick={() => navigate(`/offertes/${offerteId}/edit`)}>
          Bewerken
        </button>
        <button className="btn btn-success" onClick={handleGenereerPDF} disabled={generatingPDF}>
          {generatingPDF ? 'Opslaan...' : 'Opslaan'}
        </button>
        <button className="btn btn-secondary" onClick={handlePrint}>
          Afdrukken
        </button>
        <button className="btn btn-primary" onClick={handleVerzenden} disabled={verzending}>
          {verzending ? 'Verzenden...' : 'Verzenden'}
        </button>
      </div>

      {/* Success/Error Messages */}
      <Toast message={verzendSuccess} type="success" onClose={() => setVerzendSuccess(null)} />
      <Toast message={verzendError} type="error" onClose={() => setVerzendError(null)} />

      {/* Goedkeuringsgegevens */}
      {offerte.goedgekeurd_op && offerte.voorwaarden_akkoord && (
        <div className="print-hide" style={{
          backgroundColor: '#dcfce7',
          border: '1px solid #16a34a',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem'
        }}>
          <h3 style={{ margin: '0 0 0.75rem 0', color: '#166534' }}>Digitale Ondertekening Klant</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', color: '#166534' }}>
            <strong>Ondertekend door:</strong>
            <span>{offerte.goedgekeurd_door}</span>
            <strong>Datum/tijd:</strong>
            <span>{new Date(offerte.goedgekeurd_op).toLocaleString('nl-NL')}</span>
            <strong>IP-adres:</strong>
            <span>{offerte.goedgekeurd_ip}</span>
            <strong>Voorwaarden geaccepteerd:</strong>
            <span>Ja</span>
          </div>
        </div>
      )}
      {offerte.goedgekeurd_op && !offerte.voorwaarden_akkoord && (
        <div className="print-hide" style={{
          backgroundColor: '#dbeafe',
          border: '1px solid #3b82f6',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem'
        }}>
          <h3 style={{ margin: '0 0 0.75rem 0', color: '#1e40af' }}>Intern Goedgekeurd</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', color: '#1e40af' }}>
            <strong>Goedgekeurd door:</strong>
            <span>{offerte.goedgekeurd_door}</span>
            <strong>Datum/tijd:</strong>
            <span>{new Date(offerte.goedgekeurd_op).toLocaleString('nl-NL')}</span>
          </div>
        </div>
      )}

      {/* Render pages */}
      <div className="offerte-pages-container">
        {renderPages()}
      </div>

      {/* Verzend Modal */}
      <VerzendModal
        isOpen={showVerzendModal}
        onClose={() => setShowVerzendModal(false)}
        onConfirm={handleConfirmVerzenden}
        onGenereerPDF={handleConfirmGenereerPDF}
        defaultEmail={klant?.email || ''}
        offertenummer={offerte.offertenummer}
        isGeneratingPDF={generatingPDF}
      />

      {/* PDF Confirm Dialog */}
      <ConfirmDialog
        isOpen={showPDFConfirm}
        title="PDF Genereren"
        message="Wilt u de PDF genereren? Let op: Als er al een PDF bestaat voor deze offerte, wordt deze overschreven."
        confirmText="OK"
        cancelText="Annuleren"
        onConfirm={handleConfirmGenereerPDF}
        onCancel={() => setShowPDFConfirm(false)}
      />
    </div>
  );
};

export default OfferteDetail;
